import { create } from 'zustand';
import { getBookingsForCalendar } from '@/services/data/bookings';
import { getVideoContentForCalendar } from '@/services/data/videoContent';
import { replacePublicationsForVideo, getPublicationsForVideo } from '@/services/data/videoContentPublications';
import { syncPublicationsToMetricool } from '@/services/data/metricoolScheduler';
import type { CalendarEvent, ChannelPublicationTime, Channel } from '@/services/business/calendar';
import { mapVideoStatus } from '@/services/business/calendar';
import { logger } from '@/utils/logger';
import { FileText, Unplug, CheckCircle, Youtube, type LucideIcon } from 'lucide-react';
import type { ConnectedPlatform } from '@/services/data/socialConnections';
import { hasServiceAccess } from '@/services/data/roles';
import { supabase } from '@/integrations/supabase/client';

/** Formats a Date as "HH:MM" without relying on locale-specific toLocaleTimeString output.
 * toLocaleTimeString('fr-FR') can produce "09 h 30" or include invisible RTL marks depending
 * on the runtime, which breaks split(':').map(Number) parsing later. */
function formatHHMM(date: Date): string {
  return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
}

// Helper function to create content event from video publications
function createContentEventFromPublications(
  video: any,
  dateKey: string,
  pubs: Array<{ channel: string; scheduled_at: string }>,
  dbPublications: any[],
  isMultihook: boolean
): CalendarEvent | null {
  // For multihook content, exclude YouTube from content card (YouTube is for AB testing).
  // Disabled channels are also excluded — they must not appear in the calendar card badge.
  const filteredPubs = pubs.filter(p =>
    (p as any).status !== 'paused' &&
    (!isMultihook || p.channel.toLowerCase() !== 'youtube')
  );

  const channels = filteredPubs.map(p => p.channel) as Channel[];

  // Publications for this specific date (for card display) - excluding YouTube for multihook
  const currentDatePublications: ChannelPublicationTime[] = filteredPubs.map(p => ({
    channel: p.channel as Channel,
    date: new Date(p.scheduled_at),
    time: formatHHMM(new Date(p.scheduled_at)),
    pubId: (p as any).id,
    pubStatus: (p as any).status || 'scheduled'
  }));

  // All publications across all dates (for modal editing) - keep all including YouTube
  const allPublicationTimes: ChannelPublicationTime[] = dbPublications.map((p: any) => ({
    channel: p.channel as Channel,
    date: new Date(p.scheduled_at),
    time: formatHHMM(new Date(p.scheduled_at)),
    pubId: p.id,
    pubStatus: p.status || 'scheduled'
  }));

  // Only add content event if there are channels to show (after filtering)
  if (channels.length === 0 && isMultihook) {
    return null;
  }

  const isDraft = video.status === 'client_review' && dbPublications.every((p: any) => p.status === 'paused');

  // Override contentStatus per-card: if all pubs for this date are published, show as published
  const allCurrentPubsPublished = currentDatePublications.length > 0 && currentDatePublications.every(p => p.pubStatus === 'published');
  const cardContentStatus = allCurrentPubsPublished ? 'published' as const : mapVideoStatus(video.status);

  return {
    id: video.id,
    calendarId: `${video.id}_${dateKey}`,
    date: (() => { const [y, m, d] = dateKey.split('-').map(Number); return new Date(y, m - 1, d); })(),
    type: 'content',
    title: video.title,
    description: video.description || undefined,
    contentStatus: cardContentStatus,
    isDraft,
    channels: channels.length > 0 ? channels : undefined,
    publicationTimes: allPublicationTimes, // Full list for modal
    currentDatePublications: currentDatePublications.length > 0 ? currentDatePublications : undefined,
    videoUrl: video.video_1_url || undefined,
    coverUrl: video.video_1_thumbnail || undefined,
    video2Url: video.video_2_url || undefined,
    video2Thumbnail: video.video_2_thumbnail || undefined,
    video3Url: video.video_3_url || undefined,
    video3Thumbnail: video.video_3_thumbnail || undefined,
    isMultihook: video.is_multihook,
    viralityPredicted: video.virality_predicted || null,
    instagramCrosspostDisabled: video.instagram_crosspost_disabled === true,
    versionAEnabled: video.version_a_enabled !== false,
    versionBEnabled: video.version_b_enabled !== false,
    versionCEnabled: video.version_c_enabled !== false,
  };
}

// Helper function to create AB Testing event from video
function createAbTestingEvent(
  video: any,
  youtubePublicationForAbTesting: { channel: string; scheduled_at: string },
  dbPublications: any[]
): CalendarEvent {
  // AB testing card appears 1 day before the YouTube publication date.
  // YouTube is stored in the DB at the same time as other networks; the -1 day offset
  // matches the "24h avant la 1ère diffusion" logic used in ContentDetailModal.
  const rawAbDate = new Date(youtubePublicationForAbTesting.scheduled_at);
  const abTestingDate = new Date(rawAbDate.getFullYear(), rawAbDate.getMonth(), rawAbDate.getDate() - 1);

  // Find YouTube publications for AB Testing display
  const youtubePublications = dbPublications
    .filter((p: any) => p.channel.toLowerCase() === 'youtube')
    .map((p: any) => ({
      channel: 'youtube' as Channel,
      date: new Date(p.scheduled_at),
      time: formatHHMM(new Date(p.scheduled_at)),
      pubId: p.id,
      pubStatus: p.status || 'scheduled'
    }));

  const isDraft = video.status === 'client_review' && dbPublications.every((p: any) => p.status === 'paused');

  // Build all publication times (all channels) for the modal — same as regular content events
  const allPublicationTimes: ChannelPublicationTime[] = dbPublications.map((p: any) => ({
    channel: p.channel as Channel,
    date: new Date(p.scheduled_at),
    time: formatHHMM(new Date(p.scheduled_at)),
    pubId: p.id,
    pubStatus: p.status || 'scheduled'
  }));

  // Override contentStatus for AB card: if all YouTube pubs are published, show as published
  const allYoutubePublished = youtubePublications.length > 0 && youtubePublications.every(p => p.pubStatus === 'published');
  const abContentStatus = allYoutubePublished ? 'published' as const : mapVideoStatus(video.status);

  return {
    id: `${video.id}_ab`,
    calendarId: `${video.id}_ab`,
    date: abTestingDate,
    type: 'ab_testing',
    title: video.title,
    description: video.description || undefined,
    contentStatus: abContentStatus,
    isDraft,
    channels: ['youtube'] as Channel[],
    currentDatePublications: youtubePublications.length > 0 ? youtubePublications : undefined,
    publicationTimes: allPublicationTimes.length > 0 ? allPublicationTimes : undefined,
    videoUrl: video.video_1_url || undefined,
    coverUrl: video.video_1_thumbnail || undefined,
    video2Url: video.video_2_url || undefined,
    video2Thumbnail: video.video_2_thumbnail || undefined,
    video3Url: video.video_3_url || undefined,
    video3Thumbnail: video.video_3_thumbnail || undefined,
    isMultihook: true,
    viralityPredicted: video.virality_predicted || null,
    isAbTesting: true,
    linkedContentId: video.id
  };
}

// Helper function to process video content and create calendar events
function processVideoContent(video: any): CalendarEvent[] {
  const events: CalendarEvent[] = [];
  const dbPublications = video.video_content_publications || [];
  const isMultihook = video.is_multihook === true;

  // Group publications by date to create separate calendar events per date
  const publicationsByDate = new Map<string, Array<{ channel: string; scheduled_at: string }>>();

  dbPublications.forEach((pub: any) => {
    // Disabled channels must not drive calendar date grouping
    if (pub.status === 'paused') return;
    const pubDate = new Date(pub.scheduled_at);
    const dateKey = `${pubDate.getFullYear()}-${String(pubDate.getMonth() + 1).padStart(2, '0')}-${String(pubDate.getDate()).padStart(2, '0')}`;
    if (!publicationsByDate.has(dateKey)) {
      publicationsByDate.set(dateKey, []);
    }
    publicationsByDate.get(dateKey)!.push(pub);
  });

  // Skip videos without publications - they should not appear in the calendar
  if (publicationsByDate.size === 0) {
    return events;
  }

  // Find YouTube publication directly for AB Testing (date is already calculated by backend)
  let youtubePublicationForAbTesting: { channel: string; scheduled_at: string } | null = null;
  if (isMultihook && dbPublications.length > 0) {
    youtubePublicationForAbTesting = dbPublications.find(
      (pub: any) => pub.channel.toLowerCase() === 'youtube'
    ) || null;
  }

  // Create one event per unique publication date
  publicationsByDate.forEach((pubs, dateKey) => {
    const contentEvent = createContentEventFromPublications(video, dateKey, pubs, dbPublications, isMultihook);
    if (contentEvent) {
      events.push(contentEvent);
    }
  });

  // For multihook content, create ONE AB Testing card using YouTube date from DB directly
  if (isMultihook && youtubePublicationForAbTesting) {
    events.push(createAbTestingEvent(video, youtubePublicationForAbTesting, dbPublications));
  }

  return events;
}

export interface Task {
  id: string;
  label: string;
  icon: LucideIcon;
  onClick: () => void;
  deadlineDays?: number;
  deadlineHours?: number;
}

// Task category types for digest sidebar
export interface TaskDigestCategory {
  id: string;
  label: string;
  icon: LucideIcon;
  count: number;
  onClick: () => void;
  nextDeadline?: Date;
  items?: Array<{
    id: string;
    label: string;
    icon?: React.ComponentType<{ className?: string }>;
    onClick: () => void;
    deadline?: Date;
  }>;
  expandable?: boolean;
  isNetworkCategory?: boolean;
}

export interface AutoValidatedVideo {
  id: string;
  title: string | null;
  auto_validated_at: string | null;
  video_1_thumbnail: string | null;
  coverUrl?: string;
}

interface CalendarState {
  events: CalendarEvent[];
  autoValidatedVideos: AutoValidatedVideo[];
  loading: boolean;
  error: Error | null;
  taskDigestCategories: TaskDigestCategory[];
  initialExpandedCategories: string[];
  showNetworkPanel: boolean;
  closeVideoPanelCallback: (() => void) | null;
  
  fetchCalendarEvents: (userId: string) => Promise<void>;
  updateVideoPublications: (videoId: string, publications: ChannelPublicationTime[], userId?: string) => Promise<boolean>;
  clearEvents: () => void;
  setTaskDigestCategories: (categories: TaskDigestCategory[], expanded?: string[]) => void;
  setShowNetworkPanel: (show: boolean) => void;
  setCloseVideoPanelCallback: (callback: (() => void) | null) => void;
  closeVideoPanel: () => void;
}

/**
 * Determines whether a booking is a shooting or an editorial meeting.
 * Checks in priority order: meeting_type column → metadata.type → event_type_slug → staff assignment.
 */
function determineMeetingType(booking: any): 'shooting' | 'editorial' {
  if (booking.meeting_type === 'editorial') return 'editorial';
  if (booking.meeting_type === 'shooting') return 'shooting';
  const meta = booking.metadata as any;
  if (meta?.type === 'preparation' || meta?.type === 'editorial') return 'editorial';
  if (meta?.type === 'shooting') return 'shooting';
  const slug = (booking.event_type_slug ?? '').toLowerCase();
  if (slug.includes('pbm') || slug.includes('prep') || slug.includes('editorial')) return 'editorial';
  if (booking.personal_brand_manager_id && !booking.director_id) return 'editorial';
  return 'shooting';
}

export const useCalendarStore = create<CalendarState>((set, get) => ({
  events: [],
  autoValidatedVideos: [],
  loading: false,
  error: null,
  taskDigestCategories: [],
  initialExpandedCategories: [],
  showNetworkPanel: false,
  closeVideoPanelCallback: null,

  fetchCalendarEvents: async (userId: string) => {
    set({ loading: true, error: null });

    try {
      // Check if user has video_access permission
      const accessResult = await hasServiceAccess(userId, 'video_access');
      const hasVideoAccess = accessResult.success && accessResult.data === true;
      
      const bookingsResult = await getBookingsForCalendar(userId);
      const videosResult = hasVideoAccess ? await getVideoContentForCalendar(userId) : { success: true, data: [] };

      if (!bookingsResult.success) {
        throw bookingsResult.error || new Error('Failed to fetch bookings');
      }

      if (!videosResult.success) {
        throw videosResult.error || new Error('Failed to fetch videos');
      }

      const events: CalendarEvent[] = [];

      if (bookingsResult.data) {
        const bookings = bookingsResult.data;
        const shootings = bookings.filter((b: any) => {
          const metadata = b.metadata as any;
          return metadata?.type === 'shooting' || !metadata?.type;
        });

        bookings.forEach((booking: any) => {
          const metadata = booking.metadata as any;
          
          const type = determineMeetingType(booking);
          
          const locationData = booking.locations;
          const directorData = booking.team_film_directors;
          
          let relatedShootingDate: Date | undefined;
          
          if (type === 'editorial') {
            // Use linked shooting bookings if available
            if (booking.linkedShootingBookings && booking.linkedShootingBookings.length > 0) {
              // Get the earliest shooting booking date
              const shootingDates = booking.linkedShootingBookings
                .map((sb: any) => sb.start_time ? new Date(sb.start_time) : null)
                .filter((date: Date | null): date is Date => date !== null);
              
              if (shootingDates.length > 0) {
                shootingDates.sort((a, b) => a.getTime() - b.getTime());
                relatedShootingDate = shootingDates[0];
              }
            } else {
              // Fallback to heuristic: find next shooting after editorial date
              const editorialDate = new Date(booking.start_time);
              const relatedShooting = shootings.find((s: any) => {
                const shootingDate = new Date(s.start_time);
                return shootingDate > editorialDate;
              });
              
              if (relatedShooting) {
                relatedShootingDate = new Date(relatedShooting.start_time);
              }
            }
          }
          
          const studioData = booking.studios;
          const studioLocation = studioData?.locations;
          
          let finalLocation: string | undefined;
          let finalLocationAddress: string | undefined;
          
          if (type === 'editorial') {
            finalLocation = booking.meeting_url;
          } else {
            const location = studioLocation || locationData;
            finalLocation = location?.name;
            
            if (location) {
              const parts = [];
              if (location.address) parts.push(location.address);
              if (location.postal_code) parts.push(location.postal_code);
              if (location.city) parts.push(location.city);
              finalLocationAddress = parts.length > 0 ? parts.join(', ') : location.name;
            }
          }

          const productData = booking.products;
          const durationMinutes = type === 'shooting' 
            ? productData?.shooting_duration_minutes 
            : productData?.preparation_duration_minutes;
          
          const startDate = new Date(booking.start_time);
          const endDate = durationMinutes 
            ? new Date(startDate.getTime() + durationMinutes * 60000)
            : new Date(booking.end_time);
          
          const pbmData = booking.team_personal_brand_managers;
          
          // Generate title based on event type
          let eventTitle: string;
          if (type === 'shooting') {
            const directorName = directorData ? `${directorData.first_name} ${directorData.last_name}` : '';
            eventTitle = directorName ? `Tournage avec ${directorName}` : 'Tournage';
          } else if (type === 'editorial') {
            const pbmName = pbmData ? `${pbmData.first_name} ${pbmData.last_name}` : '';
            eventTitle = pbmName ? `RDV éditorial avec ${pbmName}` : 'RDV éditorial';
          } else {
            eventTitle = metadata?.title || booking.event_type_slug || 'Événement';
          }
          
          // Get linked editorial booking for shooting events
          let linkedEditorialBooking: CalendarEvent['linkedEditorialBooking'] = undefined;
          if (type === 'shooting' && booking.linkedEditorialBooking) {
            const editorial = booking.linkedEditorialBooking;
            const editorialStartDate = new Date(editorial.start_time);
            const editorialPbmData = editorial.team_personal_brand_managers;
            linkedEditorialBooking = {
              id: editorial.id,
              date: editorialStartDate,
              time: formatHHMM(editorialStartDate),
              pbmName: editorialPbmData ? `${editorialPbmData.first_name} ${editorialPbmData.last_name}` : undefined
            };
          }

          events.push({
            id: booking.id,
            date: new Date(booking.start_time),
            type: type as CalendarEvent['type'],
            title: eventTitle,
            time: formatHHMM(startDate),
            endTime: formatHHMM(endDate),
            durationMinutes: durationMinutes || undefined,
            location: finalLocation,
            locationAddress: finalLocationAddress,
            plan: booking.plan || undefined,
            directorName: directorData ? `${directorData.first_name} ${directorData.last_name}` : undefined,
            directorAvatarUrl: directorData?.avatar_url || undefined,
            pbmName: pbmData ? `${pbmData.first_name} ${pbmData.last_name}` : undefined,
            pbmAvatarUrl: pbmData?.avatar_url || undefined,
            status: booking.status === 'confirmed' ? 'upcoming' : 'pending',
            bookingUid: booking.booking_uid || undefined,
            bookingCalendlyUri: booking.booking_calendly_uri || undefined,
            relatedShootingDate,
            linkedEditorialBooking,
            shootingId: booking.shooting_id || undefined
          });
        });
      }

      // Collect auto-validated (to_publish) videos for the task digest sidebar,
      // AND process ALL videos into calendar events (to_publish may have future scheduled dates).
      const autoValidatedVideos: any[] = [];
      if (videosResult.data) {
        videosResult.data.forEach((video: any) => {
          if (video.status === 'to_publish') {
            autoValidatedVideos.push(video);
          }
          const videoEvents = processVideoContent(video);
          events.push(...videoEvents);
        });
      }

      set({ events, autoValidatedVideos, loading: false, error: null });
    } catch (error) {
      logger.error('Error fetching calendar events:', error);
      set({ 
        events: [], 
        autoValidatedVideos: [],
        loading: false, 
        error: error instanceof Error ? error : new Error('Unknown error') 
      });
    }
  },

  updateVideoPublications: async (videoId: string, publications: ChannelPublicationTime[], userId?: string) => {
    try {
      // Transform ChannelPublicationTime[] to the format expected by DAL
      const publicationsForDb = publications.map(pub => {
        const scheduledDate = new Date(pub.date);
        const [hours, minutes] = pub.time.split(':').map(Number);
        scheduledDate.setHours(hours, minutes, 0, 0);

        return {
          channel: pub.channel,
          scheduled_at: scheduledDate.toISOString(),
          // Preserve 'paused' status so deactivated channels survive modal re-opens
          ...(pub.pubStatus === 'paused' ? { status: 'paused' } : {}),
        };
      });

      // Disabled channels must not be synced to Metricool
      const metricoolPublicationsForDb = publicationsForDb.filter(p => p.status !== 'paused');

      // Fetch existing publications before replacing (needed for Metricool sync)
      const existingPubsResult = await getPublicationsForVideo(videoId);
      const existingPubs = existingPubsResult.success && existingPubsResult.data
        ? existingPubsResult.data.map(p => ({
            id: p.id,
            channel: p.channel as Channel,
            scheduled_at: p.scheduled_at,
            metricool_post_id: p.metricool_post_id
          }))
        : [];

      // Save to DB first — this is what the user needs confirmation for
      const result = await replacePublicationsForVideo(videoId, publicationsForDb);

      if (result.success) {
        // Run Metricool sync, webhook, and refetch in background (don't block UI)
        (async () => {
          try {
            if (userId) {
              const metricoolResult = await syncPublicationsToMetricool(
                userId,
                videoId,
                metricoolPublicationsForDb.map(p => ({ channel: p.channel, scheduledAt: p.scheduled_at })),
                existingPubs
              );
              if (!metricoolResult.success) {
                logger.warn('Metricool sync had errors:', metricoolResult.errors);
              }
            }

            supabase.functions.invoke('publication-dates-webhook', {
              body: { video_content_id: videoId, user_id: userId }
            }).catch(err => logger.warn('Publication dates webhook failed:', err));

            if (userId) {
              await get().fetchCalendarEvents(userId);
            }
          } catch (err) {
            logger.warn('Background sync after publication save failed:', err);
          }
        })();
      }

      return result.success;
    } catch (error) {
      logger.error('Error updating video publications in store:', error);
      return false;
    }
  },

  clearEvents: () => {
    set({ events: [], autoValidatedVideos: [], error: null });
  },

  setTaskDigestCategories: (categories: TaskDigestCategory[], expanded?: string[]) => {
    set({ 
      taskDigestCategories: categories,
      initialExpandedCategories: expanded || []
    });
  },

  setShowNetworkPanel: (show: boolean) => {
    set({ showNetworkPanel: show });
  },

  setCloseVideoPanelCallback: (callback: (() => void) | null) => {
    set({ closeVideoPanelCallback: callback });
  },

  closeVideoPanel: () => {
    const callback = get().closeVideoPanelCallback;
    if (callback) {
      callback();
    }
  },
}));

export const generateEventTasks = (
  event: CalendarEvent,
  options: {
    isKnowledgeBaseComplete: boolean;
    disconnectedNetworks: string[];
    connectedPlatforms: ConnectedPlatform[];
    onShowKnowledgeWizard: () => void;
    onShowNetworkPanel: (eventId: string | null) => void;
    onSelectEvent: (event: CalendarEvent) => void;
    currentNetworkPanelEventId: string | null;
  }
): Task[] => {
  const tasks: Task[] = [];
  const {
    isKnowledgeBaseComplete,
    disconnectedNetworks,
    connectedPlatforms,
    onShowKnowledgeWizard,
    onShowNetworkPanel,
    onSelectEvent,
    currentNetworkPanelEventId
  } = options;

  if (event.type === 'editorial' && !isKnowledgeBaseComplete) {
    tasks.push({
      id: 'kb',
      label: 'Compléter votre base de connaissance',
      icon: FileText,
      onClick: onShowKnowledgeWizard,
      deadlineDays: 1
    });
  }

  const eventChannels = event.channels || [];
  const eventDisconnectedNetworks = disconnectedNetworks.filter(
    network => eventChannels.includes(network as Channel)
  );

  if (event.type === 'content' && eventDisconnectedNetworks.length > 0 && !event.isDraft) {
    const networkCount = eventDisconnectedNetworks.length;
    tasks.push({
      id: 'connect-networks',
      label: networkCount === 1 
        ? `Connectez votre réseau (1)` 
        : `Connectez vos réseaux (${networkCount})`,
      icon: Unplug,
      onClick: () => {
        onShowNetworkPanel(currentNetworkPanelEventId === event.id ? null : event.id);
      },
      deadlineHours: 1
    });
  }

  if (event.type === 'content' && event.contentStatus === 'to_validate' && !event.isDraft) {
    tasks.push({
      id: 'validate-video',
      label: 'Valider cette vidéo',
      icon: CheckCircle,
      onClick: () => {
        onSelectEvent(event);
      },
      deadlineHours: 1
    });
  }

  if (event.type === 'ab_testing' && !connectedPlatforms.some(p => p.platform === 'youtube' && p.connected)) {
    tasks.push({
      id: 'connect-youtube',
      label: 'Connecter YouTube',
      icon: Youtube,
      onClick: () => {
        onShowNetworkPanel(currentNetworkPanelEventId === event.id ? null : event.id);
      },
      deadlineHours: 1
    });
  }

  return tasks;
};

